import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

export interface FreeAgentFilters {
  nameLike?: string;
  positions?: string[];
  contractMin?: string;
  contractMax?: string;
  year?: number;
  lastTeam?: string;
  statuses?: string[];
  acceptingOffers?: boolean;
}

export interface UseFreeAgentsOptions {
  leagueId: number | undefined;
  filters?: FreeAgentFilters;
  page?: number;
  pageSize?: number;
  debounceMs?: number; // Debounce delay for filter changes (default: 300ms for nameLike, 0 for others)
}

export function useFreeAgents({ leagueId, filters = {}, page = 0, pageSize = 50, debounceMs = 300 }: UseFreeAgentsOptions) {
  // Separate debounced nameLike from other filters
  const [debouncedNameLike, setDebouncedNameLike] = useState(filters.nameLike || '');
  
  // Debounce nameLike filter
  useEffect(() => {
    // Update immediately if nameLike is empty (no debounce for clearing)
    if (!filters.nameLike || filters.nameLike.trim() === '') {
      setDebouncedNameLike('');
      return;
    }
    
    const timer = setTimeout(() => {
      setDebouncedNameLike(filters.nameLike || '');
    }, debounceMs);
    
    return () => clearTimeout(timer);
  }, [filters.nameLike, debounceMs]);
  
  // Combine debounced nameLike with other filters (which update immediately)
  const effectiveFilters = useMemo(() => ({
    ...filters,
    nameLike: debouncedNameLike,
  }), [filters, debouncedNameLike]);
  
  // Clean filters - remove empty values and format arrays
  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, unknown> = {
      ...effectiveFilters,
      page,
      pageSize,
    };
    
    // Remove empty filters
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === undefined || cleaned[key] === '' || cleaned[key] === null) {
        delete cleaned[key];
      }
    });
    
    // Format arrays for API (comma-separated strings)
    if (cleaned.positions && Array.isArray(cleaned.positions) && cleaned.positions.length > 0) {
      cleaned.positions = cleaned.positions.join(',');
    }
    if (cleaned.statuses && Array.isArray(cleaned.statuses) && cleaned.statuses.length > 0) {
      cleaned.statuses = cleaned.statuses.join(',');
    }
    
    return cleaned;
  }, [effectiveFilters, page, pageSize]);

  const queryKey = ['freeAgents', leagueId, cleanFilters];

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await apiClient.get(`/roster/free-agents/${leagueId}`, { params: cleanFilters });
      return res.data;
    },
    enabled: !!leagueId,
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
} 